// [claude-code 2026-04-04] Fix SSE reconnection — don't kill listener on transient errors
// [claude-code 2026-04-03] Hook for managing in-app tool approval requests from Harper
/**
 * useToolApprovals
 * Listens to the cognition SSE stream for tool-approval-needed events.
 * Manages pending approvals and sends decisions to the backend.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { API_BASE_URL } from "../constants.js";
import { getAccessToken } from "../../../lib/supabase.js";

export interface ToolApprovalRequest {
  approvalId: string;
  toolName: string;
  toolInput: Record<string, unknown>;
  description: string;
  persistDecision?: boolean;
  status: "pending" | "approved" | "denied";
  decidedAt?: number;
}

export type AddToolApprovalResponse = (response: {
  id: string;
  approved: boolean;
  reason?: string;
}) => void | PromiseLike<void>;

export function useToolApprovals(
  requestId: string | null,
  addToolApprovalResponse?: AddToolApprovalResponse,
) {
  const [approvals, setApprovals] = useState<ToolApprovalRequest[]>([]);
  const esRef = useRef<EventSource | null>(null);
  // Track which requestIds we've already subscribed to
  const subscribedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!requestId || subscribedRef.current === requestId) return;

    // Approval cards listen directly; the visual cognition panel is no longer
    // mounted in chat streams.
    subscribedRef.current = requestId;

    const es = new EventSource(
      `${API_BASE_URL}/api/ai/cognition/stream?requestId=${encodeURIComponent(requestId)}`,
    );
    esRef.current = es;

    es.addEventListener("step", (e) => {
      try {
        const step = JSON.parse(e.data);

        if (step.kind === "tool-approval-needed" && step.detail) {
          const detail = JSON.parse(step.detail);
          setApprovals((prev) => {
            // Deduplicate
            if (prev.some((a) => a.approvalId === detail.approvalId))
              return prev;
            return [
              ...prev,
              {
                approvalId: detail.approvalId,
                toolName: detail.toolName,
                toolInput: detail.toolInput,
                description: detail.description,
                persistDecision: detail.persistDecision !== false,
                status: "pending",
              },
            ];
          });
          window.dispatchEvent(
            new CustomEvent("fintheon:tool-approval-request", {
              detail: {
                type: "tool-approval-request",
                needsApproval: true,
                id: detail.approvalId,
                toolName: detail.toolName,
                input: detail.toolInput,
              },
            }),
          );
        }

        if (step.kind === "tool-approval-resolved" && step.detail) {
          const detail = JSON.parse(step.detail);
          setApprovals((prev) =>
            prev.map((a) =>
              a.approvalId === detail.approvalId
                ? { ...a, status: detail.decision, decidedAt: Date.now() }
                : a,
            ),
          );
        }
      } catch {
        /* ignore malformed */
      }
    });

    es.addEventListener("done", () => {
      es.close();
    });

    // Reconnect on transient errors instead of dying silently.
    // EventSource auto-reconnects by default, but if readyState is CLOSED
    // (server sent a non-retryable error), we manually reopen after a delay.
    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        es.close();
        // Reopen after 2s — the approval gate may still be waiting
        const timer = setTimeout(() => {
          if (subscribedRef.current === requestId) {
            subscribedRef.current = null; // allow re-subscribe
          }
        }, 2000);
        return () => clearTimeout(timer);
      }
      // CONNECTING state = browser is auto-reconnecting, let it
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [requestId]);

  // Clear approvals when requestId changes (new message)
  useEffect(() => {
    if (!requestId) {
      setApprovals([]);
      subscribedRef.current = null;
    }
  }, [requestId]);

  const sendDecision = useCallback(
    async (approvalId: string, decision: "approved" | "denied") => {
      // Optimistically update UI
      setApprovals((prev) =>
        prev.map((a) =>
          a.approvalId === approvalId
            ? { ...a, status: decision, decidedAt: Date.now() }
            : a,
        ),
      );

      try {
        try {
          await addToolApprovalResponse?.({
            id: approvalId,
            approved: decision === "approved",
          });
        } catch {
          /* legacy approval cards can predate AI SDK approval parts */
        }
        const token = await getAccessToken();
        if (!token) throw new Error("Sign in to decide this approval.");
        const response = await fetch(
          `${API_BASE_URL}/api/harper/tool-decision`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ approvalId, decision }),
          },
        );
        if (!response.ok)
          throw new Error(`Approval failed (${response.status})`);
      } catch (err) {
        console.error("[useToolApprovals] Failed to send decision:", err);
        // Revert on failure
        setApprovals((prev) =>
          prev.map((a) =>
            a.approvalId === approvalId
              ? { ...a, status: "pending", decidedAt: undefined }
              : a,
          ),
        );
      }
    },
    [addToolApprovalResponse],
  );

  /** Approve a tool for the rest of this session (not permanent) */
  const sessionApproved = useRef(new Set<string>());
  const approveAllForSession = useCallback(
    async (approvalId: string, toolName: string) => {
      sessionApproved.current.add(toolName);
      await sendDecision(approvalId, "approved");
    },
    [sendDecision],
  );

  return { approvals, sendDecision, approveAllForSession };
}
